import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import api from '../lib/axios';
import Button from '../components/ui/Button';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const ClientAccess = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const { theme } = useTheme();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/assessments/access/${token}`);
      setAssessment(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = () => {
    if (assessment) {
      localStorage.setItem('assessmentToken', token);
      navigate(`/assessment/${assessment.id}/welcome`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.surface }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: theme.primary }} />
          <p className="text-lg" style={{ color: theme.text }}>Verifying your link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-md w-full">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Link Expired</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Please contact your advisor for a new assessment link.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: theme.surface }}>
      <div className="max-w-2xl w-full">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-center" style={{ color: theme.text }}>
              Investment Assessment
            </h2>
            <p className="mt-2 text-center text-lg" style={{ color: theme.textSecondary }}>
              Welcome, {assessment?.client?.full_name}
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="p-4 rounded-lg" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
                <h3 className="font-semibold mb-3" style={{ color: theme.text }}>What to Expect</h3>
                <ul className="space-y-2 text-sm" style={{ color: theme.textSecondary }}>
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span>This assessment helps us understand your investment needs and preferences</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span>It should take approximately 15-20 minutes to complete</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span>Your answers will help your advisor recommend suitable investment strategies</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span>This is not a final investment recommendation - your advisor will discuss the results with you</span>
                  </li>
                </ul>
              </div>

              <Button onClick={handleStart} className="w-full" size="lg">
                Start Assessment
              </Button>

              <p className="text-xs text-center" style={{ color: theme.textSecondary }}>
                Your information is secure and will only be used for this assessment.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientAccess;
